import { z } from "zod";

/**
 * Учебник в «Материалах» открывается книгой (`docs/pdf-book-view-design.md`,
 * 4.1): ответ `GET` и `POST /v1/media/files/:fileId/pages`.
 *
 * ⚠️ **Состояние считает сервер, и только он** (`bookStateOf` в
 * `apps/api/src/import/book-state.ts`): веб не классифицирует коды неудачи и
 * не решает, есть ли смысл в повторе, для этого поле `retryable`. Перечисление
 * ниже сверяется с `BOOK_STATES` сервера тестом
 * (`apps/api/src/routes/media/book-contract.test.ts`): общий пакет не может
 * импортировать из API.
 */

/**
 * - `ready`: число страниц известно, страницы готовы все;
 * - `counting`: число ещё не известно, сервер его добывает;
 * - `probing`: страницы готовятся (идёт разбор);
 * - `failed`: неудача документа, `errorCode` и `retryable` говорят, какая;
 * - `none`: страницы не готовились, их заводит `POST`.
 */
export const BookState = z.enum(["ready", "counting", "probing", "failed", "none"]);
export type BookState = z.infer<typeof BookState>;

export const BookPage = z.object({
  no: z.number().int().positive(),
  widthPt: z.number(),
  heightPt: z.number(),
  /** Миниатюра готова: плитку можно показать и открыть крупно. */
  hasThumb: z.boolean(),
  /** Страница нулевого размера: в файле она повреждена, крупно не открывается. */
  broken: z.boolean(),
});
export type BookPage = z.infer<typeof BookPage>;

export const MediaBook = z.object({
  fileId: z.string().uuid(),
  folderId: z.string().uuid().nullable(),
  name: z.string(),
  state: BookState,
  /** Страниц в документе; пока число неизвестно, у идущего разбора его число. */
  totalPages: z.number().int().positive().nullable(),
  /** Готово страниц: у идущего разбора его ход, иначе известные строки. */
  donePages: z.number().int().nonnegative(),
  /** Сколько PDF в очереди впереди идущего разбора; без разбора 0. */
  queueAhead: z.number().int().nonnegative(),
  errorCode: z.string().nullable(),
  /** Есть ли смысл в «Попробовать ещё раз». Считает сервер (`bookStateOf`), веб не классифицирует. */
  retryable: z.boolean(),
  maxFileBytes: z.number().int().positive(),
  maxPages: z.number().int().positive(),
  pages: z.array(BookPage),
  /** Подпись миниатюр (`/pages/:pageNo/thumb`), одна на все страницы. */
  thumbToken: z.string().nullable(),
  /** Подпись крупных страниц (`/pages/:pageNo/view`), одна на все страницы. */
  viewToken: z.string().nullable(),
  /** Момент истечения обеих подписей, мс. */
  tokensExpireAt: z.number().int().nullable(),
});
export type MediaBook = z.infer<typeof MediaBook>;
